<template>
  <button class="btn"
          type="button"
          :class="[loading ? 'btn--loading' : '',
                   onlyIconVisible ? '' : 'btn--' + theme,
                   'btn--' + this.size,
                   onlyIconVisible ? 'btn-icon--visible' : '',
                   fullWidth ? 'btn--full' : '',
                   fixedWH ? 'btn--fwh' : '']"
          :disabled="disable || loading"
          @click="onClick"
          :style="customStyle">
    <p4a-icon class="btn-icon"
              :class="loading ? 'btn--loading' : 'btn--static'"
              v-if="this.showIcon"
              :icon="this.icon"
              :style="iconStyle"></p4a-icon>
    <span class="btn__text" v-if="!this.onlyIconVisible">{{ text }}</span>
  </button>
</template>

<script>
  export default {
    name: 'Button',
    props: {
      loading: Boolean,
      showIcon: Boolean,
      icon: String,
      size: String,
      product: String,
      onlyIconVisible: Boolean,
      disable: Boolean,
      text: String,
      iconStyle: Object,
      fullWidth: Boolean,
      fixedWH: Boolean,
      specificWidth: Number,
      specificHeight: Number,
      borderColor: String,
      hoverColor: String,
      backgroundColor: String,
      hoverFontColor: String
    },
    computed: {
      customStyle () {
        return {
          '--border-color': this.borderColor,
          '--hover-color': this.hoverColor,
          '--background-color': this.backgroundColor? this.backgroundColor: 'transparent',
          '--hover-font-color': this.hoverFontColor ? this.hoverFontColor :  'white'
        }
      },
      theme () {
         return (this.borderColor || this.hoverColor || this.hoverFontColor) ? 'custom': this.product
      }
    },
    methods: {
      onClick(event) {
        this.$emit('click', event)
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import '../styling/styling.scss';

  $themes: (
    ds: ($docstream, $docstream, $white, $docstream),
    sns: ($snagstream, $snagstream, $white,  $snagstream),
    ls: ($leanstream, $leanstream, $white, leanstream),
    bs: ($bimstream, $bimstream, $white, bimstream),
    portal: ($portal, $portal, $white, portal),
    as: ($asstream, $asstream-hover, $white, $asstream-hover),
    tbq: ($tbq, $tbq, $white, $tbq),
    pending: ($pending-blue, $pending-blue, $white, $pending-blue),
    error: ($error-red, $error-red, $white,  $error-red),
    alert: ($alert-orange, $alert-orange, $white, $alert-orange),
    approved: ($approved-green, $approved-green, $white, $approved-green),
    custom: (var(--border-color), var(--hover-color), var(--hover-font-color),var(--hover-font-color))
  );

  .btn {
    color: inherit;
    cursor: pointer;
    font-weight: 400;
    text-align: center;
    position: relative;
    font-size: rem(14);
    border-radius: rem(2);
    vertical-align: middle;
    background-color: var(--background-color);
    border: rem(1) solid $dark-grey-background;

    &:hover {
      background-color: $light-grey-background;
      border: rem(1) solid $medium-grey-background;
    }

    &-icon--visible {
      width: rem(38);
      height: rem(38);
      border: none;
      border-radius: rem(2);

      .btn-icon { margin-right: 0; }

      &:hover {
        border: none;
        background-color: transparent;
      }
    }

    &--small {
      width: auto;
      min-width: rem(75);
      height: rem(25);
      font-size: rem(13);
      padding: 0 rem(15);

      .btn-icon {
        width: rem(12);
        height: rem(12);
        position: relative;
        font-size: rem(12);
        margin-right: rem(5);

        &:before { font-size: rem(12); }
      }

      &.btn-icon--visible {
        min-width: auto;
        padding: rem(6.5);

        .btn-icon {
          margin: 0;
          display: block;
        }
      }
    }

    &--medium {
      width: auto;
      min-width: rem(75);
      height: rem(38);
      font-size: rem(13);
      padding: rem(3) rem(15);

      .btn-icon {
        width: rem(18);
        height: rem(18);
        position: relative;
        font-size: rem(18);
        margin-right: rem(5);
        display: inline-block;
        margin-bottom: rem(5);
        vertical-align: middle;

        &:before { font-size: rem(18); }
      }

      &.btn-icon--visible {
        min-width: auto;
        padding: rem(10);

        .btn-icon {
          margin: 0;
          display: block;
        }
      }
    }

    &--context-menu {
      width: auto;
      min-width: rem(75);
      height: rem(25);
      font-size: rem(13);
      padding: rem(3) rem(10);

      .btn-icon {
        width: rem(15);
        height: rem(15);
        position: relative;
        font-size: rem(15);
        margin-right: rem(10);
        display: inline-block;
        margin-bottom: rem(3);
        vertical-align: middle;

        &:before { font-size: rem(15); }
      }

      &.btn-icon--visible {
        min-width: auto;
        padding: rem(10);

        .btn-icon {
          margin: 0;
          display: block;
        }
      }
    }

    &--large {
      width: auto;
      min-width: rem(150);
      height: rem(50);
      font-size: rem(14);
      padding: rem(15) rem(20);

      .btn-icon {
        width: rem(18);
        height: rem(18);
        position: relative;
        font-size: rem(18);
        margin-right: rem(5);
        display: inline-block;
        margin-bottom: rem(3);
        vertical-align: middle;

        &:before { font-size: rem(18); }
      }

      &.btn-icon--visible {
        min-width: auto;
        padding: rem(16);

        .btn-icon {
          margin: 0;
          display: block;
        }
      }
    }

    &:disabled {
      color: $medium-grey-background;
      border-color: $medium-grey-background;

      .btn-icon { color: $medium-grey-background; }

      &:hover {
        cursor: not-allowed;
        background-color: transparent;
        color: $medium-grey-background;
        border-color: $medium-grey-background;

        .btn-icon { color: $medium-grey-background; }
      }
    }

    &--transparent {
      border-color: transparent;

      &:hover {
        color: $medium-grey-font;
        border-color: transparent;
        background-color: transparent;
      }
    }

    @each $name, $colors in $themes {
      $color: nth($colors, 1);
      $backgroundHover: nth($colors, 2);
      $textColor: nth($colors, 3);
      $borderColorHover: nth($colors, 4);

      &--#{$name} {
        border-color: $color;

        .btn-icon { color: $color; }

        .btn__text { color: $color; }

        &:hover {
          color: $textColor;
          border-color: $borderColorHover;
          background-color: $backgroundHover;

          .btn-icon { color: $textColor; }

          .btn__text { color: inherit; }
        }

        &:disabled {

          .btn__text { color: $medium-grey-background; }
        }
      }
    }

    &--full { width: 100%; }

    &--fwh {
      width: rem(50);
      height: rem(50);
      padding: rem(15);

      .btn-icon {
        width: rem(50);
        height: rem(50);
        font-size: rem(17);

        &:before {
          width: rem(20);
          height: rem(20);
          font-size: rem(20);
        }
      }
    }

    &--loading {

      .btn__text { display: none; }

      .btn-icon {
        -webkit-animation: spin 1s linear infinite;
        -moz-animation: spin 1s linear infinite;
        animation: spin 1s linear infinite;

        &:before { color: $medium-grey-font; }
      }
    }
  }

  @-moz-keyframes spin { 100% { -moz-transform: rotate(360deg); } }

  @-webkit-keyframes spin { 100% { -webkit-transform: rotate(360deg); } }

  @keyframes spin { 100% { -webkit-transform: rotate(360deg); transform: rotate(360deg); } }
</style>



// WEBPACK FOOTER //
// node_modules/pro4all-ui/ui-kit/components/Button.vue